import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProvidersPage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadProviders = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/providers/search`, {
        zip_code: "10001"
      });
      setProviders(response.data);
    } catch (error) {
      console.error("Error loading providers:", error);
      toast.error("Failed to load providers");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-heading font-bold text-text-primary mb-8">
          Care Providers
        </h1>
        {/* Patient-Choice reminder — Medicare guarantees the right to choose
            your provider. Tyvena exists to support that with zero pressure. */}
        <div
          data-testid="patient-choice-reminder"
          className="rounded-2xl border border-primary/20 bg-gradient-to-br from-[#EDF7F6] to-white p-5 sm:p-6 shadow-sm mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <HeartHandshake className="w-5 h-5 text-primary" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-accent text-base sm:text-lg mb-1.5">
                This is your choice — no pressure, only support.
              </h3>
              <p className="font-body text-sm sm:text-base text-text-secondary leading-relaxed">
                Tyvena empowers families to make the right choices for the ones they love, based on their individual needs. Only support, encouragement, information, and connection — all on your terms. <span className="font-semibold text-text-primary">No one will ever contact you unless you reach out to them first.</span>
              </p>
            </div>
          </div>
        </div>
        {isLoading ? (
          <p className="text-lg text-text-secondary font-body">Loading providers...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-all duration-300 p-6"
              >
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {provider.name}
                </h3>
                <p className="text-sm text-text-secondary font-body mb-4">
                  {provider.description}
                </p>
                <div className="flex items-center gap-2 text-text-secondary text-sm">
                  <MapPin className="w-4 h-4" />
                  <span className="font-body">{provider.address}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
